import { useEffect } from 'react';

import { services } from '@/services';
import { useAuthStore } from '@/stores/authStore';
import { useGroupContextStore } from '@/stores/groupContextStore';
import { useWalletStore } from '@/stores/walletStore';
import { isWorkspaceActive } from '@/types';

/**
 * Group bootstrap hook(R45 / Beta v0.0.1 Phase 1 → R48.x BE handoff 2026-05-25 對齊)
 * -----------------------------------------------------------------------------
 * **R48.x 重要 pivot**:對齊 BE 拍板「atomic first login → GET /api/me 拿
 * `current_group_id`(預設 personal_gid)」。流程從「先打 /groups 推算 gid」改成
 * 「**先打 /me 拿 gid + wallet**,再打 /groups 填 availableGroups」。
 *
 * 流程:
 *   1. 等 auth 完成(`creativeUid` 非 null 且 `hydrating === false`)
 *   2. 若 availableGroups 已載過 → 不再 fetch(dedupe;避免 RequireAuth 多次 mount)
 *   3. **`meService.get()`** 拿 `currentGroupId + wallet`(BE handoff §1)
 *      - 有 gid → 寫進 `groupContextStore` + 寫 wallet 進 `walletStore`(per-(user,group))
 *      - 空/null → 走 fallback 三層(stored localStorage → user.currentGroupId
 *        → user.personalGroupId → 首個 ACTIVE group;R73 起最終層優先挑
 *        `isWorkspaceActive` 的 group,全非 ACTIVE 才退回 groups[0]);BE atomic
 *        first login 應給 personal_gid 預設,fallback 純 defensive(spec §2.2 +
 *        既有 R45 三層 fallback 慣例)
 *   4. **`groupsService.list()`** 填 `availableGroups`(GroupSwitcher / GroupCreate
 *      dialog 用);失敗仍允許 app boot(degraded mode)
 *
 * **錯誤處理**:
 *   - /me 失敗 → console.warn,fallback 走 user profile(degraded mode)
 *   - /groups 失敗 → console.warn,availableGroups 空,GroupSwitcher 顯空
 *   - 401 → `httpRequestWithRefresh` 已自動 `onAuthLost` redirect /login
 *
 * **fire 時機**:RequireAuth 內 chain(進 protected route 自動 fire 一次);hook
 * 本身 dedupe 不重複 fetch。
 */
export function useGroupBootstrap(): void {
  const creativeUid = useAuthStore((s) => s.creativeUid);
  const hydrating = useAuthStore((s) => s.hydrating);
  const user = useAuthStore((s) => s.user);
  const availableGroupsLoaded = useGroupContextStore((s) => s.availableGroups.length > 0);

  useEffect(() => {
    if (hydrating || !creativeUid) return;
    if (availableGroupsLoaded) return; // dedupe — 已載過不重 fetch

    let cancelled = false;
    // R48.10 — bootstrap race fix:RequireAuth 用 `bootstrapping` flag gate children render
    useGroupContextStore.getState().setBootstrapping(true);

    void (async () => {
      // Step 1:GET /api/me 拿 currentGroupId + wallet(BE handoff §1)
      let meCurrentGid: string | null = null;
      try {
        const me = await services.meService.get();
        if (cancelled) return;

        meCurrentGid = me.currentGroupId;

        // 寫 wallet 進 walletStore(per-(user, group))。`/me` 的 wallet 殼無 group_id
        // (docs/47 §1.A),mapper 已用 current_group_id 補進 wallet.groupId,故 store
        // 的 `groupId === gid` guard 不會把這筆 drop;monthlyGrantUnits 也已從
        // grant_per_period 帶出(用量% 分母正確)。
        if (me.wallet && meCurrentGid) {
          useWalletStore.getState().setWallet(meCurrentGid, me.wallet);
        }

        // 寫 currentGroupId(BE 給 → 用 BE 的;null/空 → 走下面 fallback)
        if (meCurrentGid) {
          const stored = useGroupContextStore.getState().currentGroupId;
          if (meCurrentGid !== stored) {
            useGroupContextStore.getState().setCurrentGroupId(meCurrentGid);
          }
        }
      } catch (err) {
        if (cancelled) return;
        // /me 失敗 — degraded mode,fallback 走下面 user profile 三層
        console.warn('[group-bootstrap] failed to fetch /me', err);
      }

      // Step 2:GET /api/groups 填 availableGroups(GroupSwitcher 用)
      let groups: Awaited<ReturnType<typeof services.groupsService.list>> = [];
      try {
        groups = await services.groupsService.list();
        if (cancelled) return;
        useGroupContextStore.getState().setAvailableGroups(groups);
      } catch (err) {
        if (cancelled) return;
        console.warn('[group-bootstrap] failed to fetch /groups', err);
        // 仍繼續(fallback 邏輯下面);group 沒填空就是 degraded
      }

      // Step 3:fallback — 只有 /me 沒回 currentGroupId 才需要(R48.x BE 應給,純 defensive)
      // 對齊 spec §2.2 三層 fallback,順位:stored localStorage → user.currentGroupId →
      // user.personalGroupId → groups[0]
      if (!meCurrentGid) {
        const stored = useGroupContextStore.getState().currentGroupId;
        const inList = (gid: string | null | undefined): boolean =>
          !!gid && groups.some((g) => g.gid === gid);

        let nextGid: string | null = null;
        if (inList(stored)) {
          nextGid = stored;
        } else if (inList(user?.currentGroupId)) {
          nextGid = user!.currentGroupId;
        } else if (inList(user?.personalGroupId)) {
          nextGid = user!.personalGroupId;
        } else if (groups.length > 0) {
          // BE 端 profile / /me 都沒給時的最終 fallback。R73:優先挑已啟用
          // (ACTIVE)的工作區 — PENDING_ORDER / SUSPENDED 進去消費會撞 BE 403
          // WORKSPACE_NOT_ACTIVE;全部非 ACTIVE 才退回 groups[0](degraded,
          // 至少讓 X-Current-Group 有值,UI 由 billing badge 引導完成付款)。
          nextGid = (groups.find((g) => isWorkspaceActive(g)) ?? groups[0]!).gid;
        }

        if (nextGid && nextGid !== stored) {
          useGroupContextStore.getState().setCurrentGroupId(nextGid);
        }
      }

      // R48.10 — bootstrap 結束(成功 / fallback / 全失敗都算)→ 放行 RequireAuth
      if (!cancelled) {
        useGroupContextStore.getState().setBootstrapping(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [creativeUid, hydrating, user, availableGroupsLoaded]);
}
